import { useEffect, useState } from "react";
import { message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const patientTypeOptions = [
  ["NEW", "New Resident", "Use this when the person is not yet an active resident and needs intake details captured."],
  ["RESIDENT", "Current Resident", "Use this for an existing resident who needs an annual or change review."],
];

function AddressSearchField({ googleMapsApiKey, label, loading: configLoading, name, onChange, onResolveAddress, value = "" }) {
  return (
    <label className="ncp-form-field ncp-form-field-wide assessment-address-field">
      <span>{label}</span>
      {configLoading && !googleMapsApiKey ? (
        <input type="text" disabled placeholder="Loading map address search..." />
      ) : (
        <GooglePlacesAutocompleteInput
          googleMapsApiKey={googleMapsApiKey}
          placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
          types={["address"]}
          value={value}
          onChange={(nextValue) => onChange?.(nextValue)}
          onResolvePlace={async ({ details, prediction }) => {
            const resolvedAddress = details?.formatted_address || prediction?.description || value || "";
            onChange?.(resolvedAddress);
            if (details) {
              onResolveAddress?.({
                ...addressFieldsFromPlaceDetails(details),
                formattedAddress: resolvedAddress,
              });
            }
          }}
        />
      )}
      <input name={name} type="hidden" value={value} />
    </label>
  );
}

function StandardAddressFields({ formValues, googleMapsApiKey, loading, prefix, title, updateField, updateFromEvent }) {
  const field = (name) => `${prefix}${name}`;
  const applyAddressFields = (addressFields) => {
    updateField(field("Address"), addressFields.addressLine1 || addressFields.formattedAddress || formValues[field("Address")] || "");
    updateField(field("City"), addressFields.city || "");
    updateField(field("County"), addressFields.county || "");
    updateField(field("State"), addressFields.state || "");
    updateField(field("Country"), addressFields.country || "");
    updateField(field("Zip"), addressFields.zipCode || "");
  };

  return (
    <>
      <div className="ncp-form-field-wide assessment-inline-label">{title}</div>
      <AddressSearchField
        googleMapsApiKey={googleMapsApiKey}
        label="Search address"
        loading={loading}
        name={field("Address")}
        value={formValues[field("Address")]}
        onChange={(value) => updateField(field("Address"), value)}
        onResolveAddress={applyAddressFields}
      />
      <label className="ncp-form-field">
        <span>Address</span>
        <input name={field("Address")} value={formValues[field("Address")]} onChange={updateFromEvent} />
      </label>
      <label className="ncp-form-field">
        <span>City</span>
        <input name={field("City")} value={formValues[field("City")]} onChange={updateFromEvent} />
      </label>
      <label className="ncp-form-field">
        <span>County</span>
        <input name={field("County")} value={formValues[field("County")]} onChange={updateFromEvent} />
      </label>
      <label className="ncp-form-field">
        <span>State</span>
        <input name={field("State")} value={formValues[field("State")]} onChange={updateFromEvent} />
      </label>
      <label className="ncp-form-field">
        <span>Country</span>
        <input name={field("Country")} value={formValues[field("Country")]} onChange={updateFromEvent} />
      </label>
      <label className="ncp-form-field">
        <span>Zip</span>
        <input name={field("Zip")} value={formValues[field("Zip")]} onChange={updateFromEvent} />
      </label>
    </>
  );
}

const initialProcessForm = {
  startDate: "",
  endDate: "",
  status: "Draft",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  room: "",
  residentNotes: "",
  residentAddress: "",
  residentCity: "",
  residentCounty: "",
  residentState: "",
  residentCountry: "",
  residentZip: "",
  nextOfKinFirstName: "",
  nextOfKinLastName: "",
  nextOfKinEmail: "",
  nextOfKinPhone: "",
  nextOfKinRelationship: "Daughter / PoA",
  nextOfKinAddress: "",
  nextOfKinCity: "",
  nextOfKinCounty: "",
  nextOfKinState: "",
  nextOfKinCountry: "",
  nextOfKinZip: "",
  socialWorkerFirstName: "",
  socialWorkerLastName: "",
  socialWorkerEmail: "",
  socialWorkerPhone: "",
  socialWorkerAddress: "",
  socialWorkerCity: "",
  socialWorkerCounty: "",
  socialWorkerState: "",
  socialWorkerCountry: "",
  socialWorkerZip: "",
};

const residentDisplayName = (resident = {}) =>
  resident.name || resident.fullName || `${resident.firstName || ""} ${resident.lastName || ""}`.trim() || "Unnamed resident";

const reviewRows = (rows) => rows.filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "");

const standardAddress = (values, prefix) => ({
  address: values[`${prefix}Address`],
  city: values[`${prefix}City`],
  county: values[`${prefix}County`],
  state: values[`${prefix}State`],
  country: values[`${prefix}Country`],
  zip: values[`${prefix}Zip`],
});

const standardAddressRows = (values, prefix) => [
  ["Address", values[`${prefix}Address`]],
  ["City", values[`${prefix}City`]],
  ["County", values[`${prefix}County`]],
  ["State", values[`${prefix}State`]],
  ["Country", values[`${prefix}Country`]],
  ["Zip", values[`${prefix}Zip`]],
];

export default function NewAssessmentProcessPage() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [patientType, setPatientType] = useState("RESIDENT");
  const [formValues, setFormValues] = useState(initialProcessForm);
  const [residents, setResidents] = useState([]);
  const [selectedResidentId, setSelectedResidentId] = useState("");
  const [residentsLoading, setResidentsLoading] = useState(true);
  const [residentsError, setResidentsError] = useState("");
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const selectedResident = residents.find((resident) => String(resident.id || resident.residentId) === selectedResidentId);
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const steps = [
    ["type", "Resident Type"],
    ["process", "Process Details"],
    ["resident", patientType === "NEW" ? "Resident Details" : "Current Resident"],
    ["nextOfKin", "Next of Kin"],
    ["socialWorker", "Social Worker"],
    ["preview", "Preview"],
  ];
  const activeStep = steps[stepIndex]?.[0] || "type";

  const updateField = (field, value) => {
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const updateFromEvent = (event) => {
    updateField(event.target.name, event.target.value);
  };

  const goNext = () => setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  const goBack = () => setStepIndex((current) => Math.max(current - 1, 0));

  const selectedResidentRows = selectedResident
    ? reviewRows([
        ["Resident", residentDisplayName(selectedResident)],
        ["Room", selectedResident.room || "Not set"],
        ["Age", selectedResident.age || "Not set"],
        ["Care level", selectedResident.careLevel || selectedResident.levelOfCare || "Not set"],
        ["NCP status", selectedResident.ncpStatus || selectedResident.carePlanStatus || "Not set"],
        ["Next review", selectedResident.nextReview || selectedResident.nextReviewDate || "Not set"],
        ["Primary contact", selectedResident.primaryContact || selectedResident.nextOfKinName || "Not set"],
        ["Address", selectedResident.address?.address || selectedResident.address || "Not set"],
        ["City", selectedResident.address?.city || selectedResident.city || "Not set"],
        ["County", selectedResident.address?.county || selectedResident.county || "Not set"],
        ["State", selectedResident.address?.state || selectedResident.state || "Not set"],
        ["Country", selectedResident.address?.country || selectedResident.country || "Not set"],
        ["Zip", selectedResident.address?.zip || selectedResident.address?.zipCode || selectedResident.zip || selectedResident.zipCode || "Not set"],
      ])
    : [];

  const previewSections = [
    {
      title: "Assessment Process",
      rows: reviewRows([
        ["Resident type", patientType === "NEW" ? "New Resident" : "Current Resident"],
        ["Start date", formValues.startDate],
        ["End date", formValues.endDate],
        ["Status", formValues.status],
      ]),
    },
    {
      title: patientType === "NEW" ? "Resident Details" : "Selected Resident",
      rows:
        patientType === "NEW"
          ? reviewRows([
              ["First name", formValues.firstName],
              ["Last name", formValues.lastName],
              ["Date of birth", formValues.dateOfBirth],
              ["Expected room", formValues.room],
              ["Initial notes", formValues.residentNotes],
              ...standardAddressRows(formValues, "resident"),
            ])
          : selectedResidentRows,
    },
    {
      title: "Next of Kin",
      rows: reviewRows([
        ["First name", formValues.nextOfKinFirstName],
        ["Last name", formValues.nextOfKinLastName],
        ["Email", formValues.nextOfKinEmail],
        ["Phone", formValues.nextOfKinPhone],
        ["Relationship", formValues.nextOfKinRelationship],
        ...standardAddressRows(formValues, "nextOfKin"),
      ]),
    },
    {
      title: "Social Worker",
      rows: reviewRows([
        ["First name", formValues.socialWorkerFirstName],
        ["Last name", formValues.socialWorkerLastName],
        ["Email", formValues.socialWorkerEmail],
        ["Phone", formValues.socialWorkerPhone],
        ...standardAddressRows(formValues, "socialWorker"),
      ]),
    },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...formValues,
      patientType,
      residentId: patientType === "RESIDENT" ? selectedResidentId : undefined,
      resident:
        patientType === "NEW"
          ? {
              firstName: formValues.firstName,
              lastName: formValues.lastName,
              dateOfBirth: formValues.dateOfBirth,
              room: formValues.room,
              notes: formValues.residentNotes,
              address: standardAddress(formValues, "resident"),
            }
          : undefined,
      socialWorker: {
        firstName: formValues.socialWorkerFirstName,
        lastName: formValues.socialWorkerLastName,
        email: formValues.socialWorkerEmail,
        phone: formValues.socialWorkerPhone,
        address: standardAddress(formValues, "socialWorker"),
      },
      nextOfKin: {
        firstName: formValues.nextOfKinFirstName,
        lastName: formValues.nextOfKinLastName,
        email: formValues.nextOfKinEmail,
        phone: formValues.nextOfKinPhone,
        relationship: formValues.nextOfKinRelationship,
        address: standardAddress(formValues, "nextOfKin"),
      },
    };

    try {
      const created = await careService.assessmentProcesses.create(payload);
      const createdId = created?.id || created?.assessmentProcessId;
      message.success("Assessment process started.");
      navigate(createdId ? `/assessments/${createdId}` : "/assessments");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to start assessment process.");
    }
  };

  useEffect(() => {
    let isMounted = true;
    setResidentsLoading(true);
    careService.residents
      .list()
      .then((data) => {
        if (!isMounted) return;
        const nextResidents = Array.isArray(data) ? data : [];
        setResidents(nextResidents);
        setSelectedResidentId((current) => current || String(nextResidents[0]?.id || nextResidents[0]?.residentId || ""));
        setResidentsError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setResidents([]);
        setResidentsError(err?.response?.data?.message || err?.message || "Unable to load residents.");
      })
      .finally(() => {
        if (isMounted) setResidentsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Start New Assessment Process</h1>
          <p>Create the assessment process first, then attach state assessment, MaG, preliminary plan, notes, negotiated plan, final assessment, onboarding, probation, and confirmation records as the flow progresses.</p>
        </div>
        <div className="residents-actions">
          <Link className="mini-btn" to="/assessments"><i className="ti ti-arrow-left" /> Assessments</Link>
        </div>
      </div>

      <Panel title="Assessment Process">
        <form className="assessment-page-form" onSubmit={handleSubmit}>
          <div className="assessment-stepper" aria-label="Assessment process steps">
            {steps.map(([key, label], index) => (
              <button
                className={`${index === stepIndex ? "is-active" : ""} ${index < stepIndex ? "is-complete" : ""}`}
                type="button"
                onClick={() => setStepIndex(index)}
                key={key}
              >
                <span>{index + 1}</span>
                <strong>{label}</strong>
              </button>
            ))}
          </div>

          {activeStep === "type" ? (
            <section className="assessment-drawer-section">
              <h3>Select Resident Type</h3>
              <div className="assessment-type-grid">
                {patientTypeOptions.map(([value, label, text]) => (
                  <button
                    className={patientType === value ? "is-active" : ""}
                    type="button"
                    onClick={() => {
                      setPatientType(value);
                      setStepIndex(1);
                    }}
                    key={value}
                  >
                    <strong>{label}</strong>
                    <span>{value}</span>
                    <small>{text}</small>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {activeStep === "process" ? (
            <section className="assessment-drawer-section">
              <h3>Process Details</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>Start date</span>
                  <input type="date" name="startDate" value={formValues.startDate} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>End date</span>
                  <input type="date" name="endDate" value={formValues.endDate} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Status</span>
                  <select name="status" value={formValues.status} onChange={updateFromEvent}>
                    <option>Draft</option>
                    <option>Active</option>
                    <option>In review</option>
                    <option>Confirmed</option>
                  </select>
                </label>
              </div>
            </section>
          ) : null}

          {activeStep === "resident" && patientType === "NEW" ? (
            <section className="assessment-drawer-section">
              <h3>New Resident Details</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>First name</span>
                  <input name="firstName" value={formValues.firstName} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Last name</span>
                  <input name="lastName" value={formValues.lastName} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Date of birth</span>
                  <input type="date" name="dateOfBirth" value={formValues.dateOfBirth} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Expected room</span>
                  <input name="room" value={formValues.room} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Initial resident notes</span>
                  <textarea name="residentNotes" rows={3} value={formValues.residentNotes} onChange={updateFromEvent} />
                </label>
                <StandardAddressFields
                  formValues={formValues}
                  googleMapsApiKey={googleMapsApiKey}
                  loading={appConfigLoading}
                  prefix="resident"
                  title="Resident Address"
                  updateField={updateField}
                  updateFromEvent={updateFromEvent}
                />
              </div>
            </section>
          ) : null}

          {activeStep === "resident" && patientType === "RESIDENT" ? (
            <section className="assessment-drawer-section">
              <h3>Current Resident</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Select resident</span>
                  <select name="residentId" value={selectedResidentId} onChange={(event) => setSelectedResidentId(event.target.value)}>
                    <option value="">{residentsLoading ? "Loading residents..." : "Select resident"}</option>
                    {residents.map((resident) => (
                      <option value={resident.id || resident.residentId} key={resident.id || resident.residentId}>
                        {residentDisplayName(resident)} - Room {resident.room || "Not set"}
                      </option>
                    ))}
                  </select>
                </label>
                {residentsError ? <p className="form-error">{residentsError}</p> : null}
                {selectedResident ? (
                  <>
                    <label className="ncp-form-field">
                      <span>Resident name</span>
                      <input value={residentDisplayName(selectedResident)} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Room</span>
                      <input value={selectedResident.room || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Age</span>
                      <input value={selectedResident.age || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Care level</span>
                      <input value={selectedResident.careLevel || selectedResident.levelOfCare || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>NCP status</span>
                      <input value={selectedResident.ncpStatus || selectedResident.carePlanStatus || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Next review</span>
                      <input value={selectedResident.nextReview || selectedResident.nextReviewDate || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Primary contact</span>
                      <input value={selectedResident.primaryContact || selectedResident.nextOfKinName || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Care team</span>
                      <input value={selectedResident.careTeam || selectedResident.assignedCareTeam || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Open alerts</span>
                      <input value={selectedResident.alerts || selectedResident.openAlerts || 0} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Last note</span>
                      <input value={selectedResident.lastNote || selectedResident.lastNoteDate || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Address</span>
                      <input value={selectedResident.address?.address || selectedResident.address || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>City</span>
                      <input value={selectedResident.address?.city || selectedResident.city || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>County</span>
                      <input value={selectedResident.address?.county || selectedResident.county || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>State</span>
                      <input value={selectedResident.address?.state || selectedResident.state || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Country</span>
                      <input value={selectedResident.address?.country || selectedResident.country || ""} disabled readOnly />
                    </label>
                    <label className="ncp-form-field">
                      <span>Zip</span>
                      <input value={selectedResident.address?.zip || selectedResident.address?.zipCode || selectedResident.zip || selectedResident.zipCode || ""} disabled readOnly />
                    </label>
                  </>
                ) : null}
              </div>
            </section>
          ) : null}

          {activeStep === "nextOfKin" ? (
            <section className="assessment-drawer-section">
              <h3>Next of Kin</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>First name</span>
                  <input name="nextOfKinFirstName" value={formValues.nextOfKinFirstName} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Last name</span>
                  <input name="nextOfKinLastName" value={formValues.nextOfKinLastName} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Email</span>
                  <input name="nextOfKinEmail" type="email" value={formValues.nextOfKinEmail} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Phone</span>
                  <input name="nextOfKinPhone" type="tel" value={formValues.nextOfKinPhone} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Relationship</span>
                  <select name="nextOfKinRelationship" value={formValues.nextOfKinRelationship} onChange={updateFromEvent}>
                    <option>Daughter / PoA</option>
                    <option>Son / PoA</option>
                    <option>Spouse</option>
                    <option>Guardian</option>
                    <option>Self</option>
                    <option>Other</option>
                  </select>
                </label>
                <StandardAddressFields
                  formValues={formValues}
                  googleMapsApiKey={googleMapsApiKey}
                  loading={appConfigLoading}
                  prefix="nextOfKin"
                  title="Next of Kin Address"
                  updateField={updateField}
                  updateFromEvent={updateFromEvent}
                />
              </div>
            </section>
          ) : null}

          {activeStep === "socialWorker" ? (
            <section className="assessment-drawer-section">
              <h3>Social Worker</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>First name</span>
                  <input name="socialWorkerFirstName" value={formValues.socialWorkerFirstName} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Last name</span>
                  <input name="socialWorkerLastName" value={formValues.socialWorkerLastName} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Email</span>
                  <input name="socialWorkerEmail" type="email" value={formValues.socialWorkerEmail} onChange={updateFromEvent} />
                </label>
                <label className="ncp-form-field">
                  <span>Phone</span>
                  <input name="socialWorkerPhone" type="tel" value={formValues.socialWorkerPhone} onChange={updateFromEvent} />
                </label>
                <StandardAddressFields
                  formValues={formValues}
                  googleMapsApiKey={googleMapsApiKey}
                  loading={appConfigLoading}
                  prefix="socialWorker"
                  title="Social Worker Address"
                  updateField={updateField}
                  updateFromEvent={updateFromEvent}
                />
              </div>
            </section>
          ) : null}

          {activeStep === "preview" ? (
            <section className="assessment-drawer-section">
              <h3>Preview and Submit</h3>
              <div className="assessment-preview-grid">
                {previewSections.map((section) => (
                  <article className="assessment-preview-card" key={section.title}>
                    <strong>{section.title}</strong>
                    <dl>
                      {section.rows.length ? section.rows.map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      )) : <p>No details captured yet.</p>}
                    </dl>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <div className="assessment-drawer-actions">
            <Link className="btn secondary" to="/assessments">Cancel</Link>
            {stepIndex > 0 ? <button className="btn secondary" type="button" onClick={goBack}>Back</button> : null}
            {activeStep === "preview" ? (
              <button className="btn primary" type="submit">Submit and Start Process</button>
            ) : (
              <button className="btn primary" type="button" onClick={goNext}>Next</button>
            )}
          </div>
        </form>
      </Panel>
    </CareShell>
  );
}
